import { createContext, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { clone } from '../utils/clone';
import { useHttpClient } from '../client/HttpClientContext';
import { useRpcClient } from '../client/useRpcClient';
import { getOwnProperty } from '../utils/getOwnProperty';
import { fetchPropertyValue } from './crmObjectsAPI';
import { makeIdentifierKey, parseIdentifierKey } from './identifierKey';
import { loadProcessingMetadata, persistProcessingMetadata } from './persistenceUtils';
import { NO_STATUS, pollSmartPropertyStatusesWithMetadata } from './pollingAPI';
import { usePollListeners } from './usePollListeners';
import { useBatchUpdateObjects } from './useBatchUpdateObjects';
import { TIME_TOKENS, UPDATE_OBJECTS_DEBOUNCE_KEY } from '../utils/timeTokens';
import { useOnCrmMessageTopic } from 'crm-message-bus/useOnCrmMessageTopic';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import { jsx as _jsx } from "react/jsx-runtime";
const UNSET = Symbol('UNSET');

// A structure to store values that override the controlled value. Values should
// be dropped from here when the controlled value is updated to something that
// doesn't match the stored controlled value.
export const AIPropertiesProcessingContext = /*#__PURE__*/createContext(null);
function processingMetadataReducer(metadata, action) {
  if (action.type === 'loadMetadata') {
    const {
      persistedMetadata
    } = action;
    return Object.assign({}, persistedMetadata, metadata);
  } else if (action.type === 'startProcessing') {
    // Set pending status for given property value coordinates
    const newState = clone(metadata);
    const {
      identifierKeys,
      startedAt
    } = action;
    for (const key of identifierKeys) {
      newState[key] = {
        status: 'pending',
        isLoadingNewValue: false,
        startedAt,
        metadata: {}
      };
    }
    return newState;
  } else if (action.type === 'handlePollingResult') {
    // Handle result from polling API and update statuses and metadata based on result
    const newState = clone(metadata);
    const {
      data
    } = action;
    for (const [key, pollResult] of Object.entries(data)) {
      if (Object.prototype.hasOwnProperty.call(newState, key)) {
        if (pollResult.status === NO_STATUS) {
          // If the polling API returns nothing for coordinates we asked for,
          // the backend isn't tracking it so the frontend will also drop the
          // metadata
          delete newState[key];
        } else {
          newState[key].status = pollResult.status;
          newState[key].metadata = pollResult.metadata;
          newState[key].smartPropertyResultId = pollResult.smartPropertyResultId;
          if (metadata[key].status !== 'completed' && pollResult.status === 'completed') {
            // If the API returns a newly completed status, we stop polling and
            // start loading the new value
            newState[key].isLoadingNewValue = true;
          }
        }
      }
    }
    return newState;
  } else if (action.type === 'finishLoadingNewValue') {
    // Handle the load of the new value after polling and value fetch has completed
    const newState = clone(metadata);
    const {
      identifierKey
    } = action;
    if (Object.prototype.hasOwnProperty.call(newState, identifierKey)) {
      newState[identifierKey].isLoadingNewValue = false;
    }
    return newState;
  }
  return metadata;
}
function AIPropertiesProcessingContextProvider({
  children
}) {
  const httpClient = useHttpClient();
  const rpcClient = useRpcClient();
  const {
    queueUpdate
  } = useBatchUpdateObjects(TIME_TOKENS[UPDATE_OBJECTS_DEBOUNCE_KEY]);
  const [metadata, metadataDispatch] = useReducer(processingMetadataReducer, {}, () => loadProcessingMetadata());
  const propertyValueRegistryRef = useRef({});
  const latestPollRef = useRef(0);
  const {
    startPollingAndAttach,
    detachPolling
  } = usePollListeners(keys => {
    latestPollRef.current += 1;
    const pollId = latestPollRef.current;
    pollSmartPropertyStatusesWithMetadata(rpcClient, keys).then(data => {
      if (latestPollRef.current !== pollId) {
        return;
      }
      metadataDispatch({
        type: 'handlePollingResult',
        data
      });
    }).catch(() => {});
  });
  useEffect(() => {
    // Get all keys that are pending a new value and don't have a value in the registry yet
    const keysToFetchValueFor = Object.keys(metadata).filter(key => metadata[key].isLoadingNewValue && !propertyValueRegistryRef.current[key]);
    for (const key of keysToFetchValueFor) {
      const {
        objectTypeId,
        propertyName,
        objectId
      } = parseIdentifierKey(key);
      propertyValueRegistryRef.current[key] = {
        controlledValue: UNSET,
        aiGeneratedValue: UNSET
      };
      fetchPropertyValue(httpClient, objectTypeId, objectId, propertyName).then(value => {
        if (Object.prototype.hasOwnProperty.call(propertyValueRegistryRef.current, key)) {
          propertyValueRegistryRef.current[key].aiGeneratedValue = value;
          metadataDispatch({
            type: 'finishLoadingNewValue',
            identifierKey: key
          });
          queueUpdate(key);
        }
      }).catch(() => {
        // Fail open if we fail to fetch a new value to allow users to use the
        // component, ie. set the loading flag to false but drop the value override
        delete propertyValueRegistryRef.current[key];
        metadataDispatch({
          type: 'finishLoadingNewValue',
          identifierKey: key
        });
      });
    }
  }, [httpClient, metadata, queueUpdate]);
  useEffect(() => {
    persistProcessingMetadata(metadata);
  }, [metadata]);
  const applyLoadedValue = useCallback((objectTypeId, propertyName, objectId, value) => {
    const key = makeIdentifierKey(objectTypeId, propertyName, objectId);
    const metadataEntry = getOwnProperty(metadata, key);
    let propertyValue = getOwnProperty(propertyValueRegistryRef.current, key);
    if (!metadataEntry || !propertyValue) {
      return value;
    }

    // Side effect: start tracking the controlled value if it's not already
    // being tracked, or drop the override if the tracked controlled value has
    // changed
    if (!metadataEntry.isLoadingNewValue) {
      if (propertyValue.controlledValue === UNSET) {
        propertyValueRegistryRef.current[key].controlledValue = value;
      } else if (propertyValue.controlledValue !== value) {
        delete propertyValueRegistryRef.current[key];
        propertyValue = undefined;
      }
    }
    return propertyValue && !metadataEntry.isLoadingNewValue && propertyValue.aiGeneratedValue !== UNSET ? propertyValue.aiGeneratedValue : value;
  }, [metadata]);
  const startProcessing = useCallback((objectTypeId, propertyName, objectIds) => {
    const identifierKeys = objectIds.map(objectId => makeIdentifierKey(objectTypeId, propertyName, objectId));
    metadataDispatch({
      type: 'startProcessing',
      identifierKeys,
      startedAt: Date.now()
    });
  }, []);
  const handleStartProcessingForSelectedProperties = useCallback(({
    objectTypeId,
    objectIds,
    propertyNames
  }) => {
    propertyNames.forEach(propertyName => {
      startProcessing(objectTypeId, propertyName, objectIds);
    });
  }, [startProcessing]);
  useOnCrmMessageTopic(TOPIC_NAMES.POLL_SMART_PROPERTIES, handleStartProcessingForSelectedProperties);
  const value = useMemo(() => ({
    metadata,
    applyLoadedValue,
    startProcessing,
    startPollingAndAttach,
    detachPolling
  }), [applyLoadedValue, detachPolling, metadata, startPollingAndAttach, startProcessing]);
  return /*#__PURE__*/_jsx(AIPropertiesProcessingContext.Provider, {
    value: value,
    children: children
  });
}

/**
 * Any AI processing hooks called in components in the tree beneath this will
 * not trigger polling for AI values and will not return a pending status.
 *
 * To be used in contexts where the property values shown are historic values
 * and not meant to show loading states.
 */
export function DisableAIPollingContext({
  children
}) {
  return /*#__PURE__*/_jsx(AIPropertiesProcessingContext.Provider, {
    value: null,
    children: children
  });
}
export default AIPropertiesProcessingContextProvider;